import { useMemo } from 'react';

import type { AccountAddressesFilters } from '../../../api/user/hooks';
import { useAccountAddresses } from '../../../api/user/hooks';
import { useUnifiedBalancesStore } from '../stores/useUnifiedBalancesStore';
import type { UnifiedBalances } from '../stores/utils/computeUnifiedBalances';

export type UnifiedBalancesInputFilters =
  | Pick<
      AccountAddressesFilters,
      | 'custody'
      | 'walletIds'
      | 'chain'
      | 'legacyWalletHandling'
      | 'includeEoaOnAllEvmChains'
      | 'walletChainPairs'
    >
  | undefined;

/**
 * Hook that provides computed unified balances, in various convenient formats.
 *
 * @param filters - Optional filters to apply to the balances (custody, chain, walletIds)
 * @returns Computed unified balances or loading state if balances are not yet available.
 */
export function useUnifiedBalances(
  filters?: UnifiedBalancesInputFilters
): UnifiedBalances {
  const addresses = useAccountAddresses(filters);

  const unifiedBalances = useUnifiedBalancesStore((state) =>
    state.selectBalances(addresses)
  );

  return useMemo(() => {
    if (!unifiedBalances) {
      return {
        balances: undefined,
        balancesByChainAddressId: undefined,
        balancesByGroupId: undefined,
        collectibleBalances: undefined,
        collectibleBalancesByChainAddressId: undefined,
        hasBalances: undefined,
        hasCollectibleBalances: undefined,
        hasNftBalances: undefined,
        hasPositionBalances: undefined,
        hasTokenBalances: undefined,
        isLoading: true as const,
        isStale: undefined,
        nftBalances: undefined,
        nftsExcludingPatrons: undefined,
        nftsExcludingPatronsByGroupId: undefined,
        patronNfts: undefined,
        patronNftsGrouped: undefined,
        positionBalances: undefined,
        positionBalancesByGroupIdByTypeByProtocolId: undefined,
        tokenBalances: undefined,
        totalNftExcludingPatronsUsdBalance: undefined,
        totalUsdBalanceIncludingNfts: undefined,
        totalUsdTokenBalance: undefined,
        withdrawablePatronGrouped: undefined,
        withdrawablePatronNfts: undefined,
      };
    }

    return unifiedBalances;
  }, [unifiedBalances]);
}
